import { Link } from 'react-router';
import { Check, CheckCircle2, Circle, X } from 'lucide-react';
import type { WorkflowStep as WorkflowStepType } from '../types';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface WorkflowStepProps {
  step: WorkflowStepType;
  completed: boolean;
  dismissed: boolean;
  onDismiss: () => void;
}

export function WorkflowStep({ step, completed, dismissed, onDismiss }: WorkflowStepProps) {
  if (dismissed) return null;

  const content = (
    <div className="flex items-start gap-3">
      <div className="shrink-0 mt-0.5">
        {completed ? (
          <CheckCircle2 className="w-6 h-6 text-sev-4" />
        ) : (
          <Circle className="w-6 h-6 text-muted-foreground" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p
              className={cn(
                'text-sm font-semibold flex flex-wrap items-center gap-2',
                completed ? 'text-muted-foreground' : null,
              )}
            >
              {step.title}
              {completed ? (
                <Badge className="bg-sev-4/15 text-sev-4 border-0 font-normal gap-1">
                  <Check className="w-3 h-3" />
                  Completed
                </Badge>
              ) : null}
              {step.optional && !completed ? (
                <Badge className="bg-sev-5/15 text-sev-5 border-0 font-normal">
                  Optional
                </Badge>
              ) : null}
            </p>
            {step.description ? (
              <p className="text-xs text-muted-foreground mt-1">{step.description}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="shrink-0 inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDismiss();
            }}
            aria-label="Dismiss step"
            title="Dismiss this step"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );

  if (completed) {
    return (
      <div className="rounded-md border border-border-subtle bg-muted/30 p-3 opacity-70">
        {content}
      </div>
    );
  }

  if (!step.path) {
    return (
      <div className="rounded-md border border-border-subtle bg-card p-3">
        {content}
      </div>
    );
  }

  return (
    <Link
      to={step.path}
      className="block rounded-md border border-border-subtle bg-card p-3 hover:bg-accent/30 transition-colors no-underline"
    >
      {content}
    </Link>
  );
}
